import React from 'react';
import { I18n } from 'react-redux-i18n';
import styled from 'styled-components';
import get from 'lodash/get';

import ExportButton from 'components/Export';
import Cell from 'components/Cell';
import Pellet from 'components/Pellet';
import Table from 'components/Table';
import Header from 'components/Table/Header';
import CardHeader from 'components/CardHeader';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  background-color: white;
`;

const Wrapper = styled.div`
  display: flex;
  gap: 1.25rem;

  @media (max-width: 1200px) {
    flex-direction: column-reverse;
  }

  .exportContainer {
    gap: 1rem;
    margin-left: auto;
    padding: 0.75rem 0;
  }
`;

const TableContainer = styled.div`
  width: 100%;
`;

const SideInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 12rem;

  @media (max-width: 1200px) {
    width: unset;
  }

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const TableSideText = styled.p`
  font-family: ${(props) => props.theme.fonts.oswald_light};
  font-size: 0.875rem;
  line-height: 1.5rem;
  color: #1a222c;

  @media (max-width: 1200px) {
    margin-top: 0;
    width: unset;
  }
`;

const TableLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  min-width: 12rem;

  @media (max-width: 1200px) {
    flex-direction: row;
  }

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const TableLegendTitle = styled.p`
  color: #1a222c;
  font-family: ${(props) => props.theme.fonts.oswald};
  font-size: 1rem;
  text-transform: uppercase;
`;

const LegendIndicator = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;

const LegendIndicatorValue = styled.p`
  font-family: ${(props) => props.theme.fonts.oswald};
  font-size: 0.75rem;
  color: #1a222c;
  text-transform: uppercase;
`;

const ColumnHeader = styled(Header).attrs(() => ({
  sortable: true
}))`
  background-color: rgba(238, 238, 238, 0.3);
`;

const ColumnCityCell = styled(Cell).attrs((props) =>
  props.showPellet
    ? {
        type: 'pellet',
        value: {
          value: props.city,
          color: getPelletColor(props.original)
        }
      }
    : { value: props.city }
)`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  text-transform: uppercase;
  font-family: ${(props) => props.theme.fonts.oswald};
  font-size: 0.75rem;
  margin-left: 1.25rem;
`;

const ColumnDataCell = styled(Cell)`
  text-transform: uppercase;
  font-family: ${(props) => props.theme.fonts.silkserif_regular};
  font-size: 1rem;
  color: #1b242c;
`;

function getPelletColor({ tp = '0%' }) {
  const val = parseFloat(tp);
  if (val < 20) {
    return '#ef6443';
  } else if (val < 40) {
    return '#323739';
  }
  return '#bcb9a0';
}

export default class TableComponent extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      sorted: [{ id: 'tp', desc: true }]
    };
  }

  handleSort(id) {
    let { sorted } = this.state;
    let desc = true;
    if (sorted.length > 0 && sorted[0].id === id) {
      desc = !sorted[0].desc;
    }
    sorted = [{ id, desc }];
    this.setState({ sorted });
  }

  getFirstColumn(isWebsitesTable) {
    return {
      Header: () => (
        <ColumnHeader
          sort={() => this.handleSort('market_name')}
          title={I18n.t('synthesisDistribution.detail.tableColumns.market')}
          styles={'border-right:0;'}
        />
      ),
      Cell: ({ value, ...rest }) => (
        <ColumnCityCell showPellet={!isWebsitesTable} city={value} {...rest} />
      ),
      accessor: 'market_name'
    };
  }

  getSecondColumn(isWebsitesTable) {
    const { selectedIndicator = {} } = this.props;
    return {
      Header: () => (
        <ColumnHeader
          sort={() => this.handleSort(isWebsitesTable ? 'facing' : 'dn')}
          title={
            isWebsitesTable
              ? I18n.t('synthesisDistribution.detail.tableColumns.nbWeb')
              : `${I18n.t(
                  'synthesisDistribution.detail.tableColumns.nb'
                )} ${get(selectedIndicator, 'value')}`
          }
          styles={`${isWebsitesTable ? 'border-right:0;' : ''}border-left:0;`}
        />
      ),
      Cell: (props) => <ColumnDataCell {...props} />,
      sortMethod: (a, b) => Number(a) - Number(b),
      accessor: isWebsitesTable ? 'facing' : 'dn'
    };
  }

  getThirdColumn() {
    return {
      Header: () => (
        <ColumnHeader
          sort={() => this.handleSort('tp')}
          title={I18n.t('synthesisDistribution.detail.tableColumns.tp')}
          styles={'border-left:0;'}
        />
      ),
      Cell: (props) => <ColumnDataCell {...props} />,
      sortMethod: (a, b) =>
        (a ? Number(a.slice(0, a.length - 1)) : 0) -
        (b ? Number(b.slice(0, b.length - 1)) : 0),
      accessor: 'tp'
    };
  }

  getTableColumns(selectedIndicator) {
    const isWebsitesTable = get(selectedIndicator, 'value') === 'websites';
    let columns = [
      this.getFirstColumn(isWebsitesTable),
      this.getSecondColumn(isWebsitesTable)
    ];
    if (!isWebsitesTable) {
      columns.push(this.getThirdColumn());
    }
    return columns;
  }

  renderLegendIndicator(text, color) {
    return (
      <LegendIndicator>
        <Pellet size="19px" color={color} />
        <LegendIndicatorValue>{text}</LegendIndicatorValue>
      </LegendIndicator>
    );
  }

  renderSideInfo() {
    return (
      <SideInfo>
        <TableSideText>
          {I18n.t('synthesisDistribution.detail.legend.explain')}
        </TableSideText>
        <TableLegend>
          <TableLegendTitle>
            {I18n.t('synthesisDistribution.detail.legend.title')} :
          </TableLegendTitle>
          {this.renderLegendIndicator(
            I18n.t('synthesisDistribution.detail.legend.tier1'),
            '#ef6443'
          )}
          {this.renderLegendIndicator(
            I18n.t('synthesisDistribution.detail.legend.tier2'),
            '#323739'
          )}
          {this.renderLegendIndicator(
            I18n.t('synthesisDistribution.detail.legend.tier3'),
            '#bcb9a0'
          )}
        </TableLegend>
      </SideInfo>
    );
  }

  render() {
    const { selectedIndicator, data, optionsExportXls } = this.props;
    const { sorted } = this.state;
    const columns = this.getTableColumns(selectedIndicator);

    return (
      <Container id="synthese-table-dn-tp">
        <CardHeader
          title={I18n.t('synthesisDistribution.detail.titleTable')}
          fontSize="1.25rem"
          iconRight="right"
          rightComponents={
            <ExportButton
              name={`${get(this.props.selectedIndicator, 'label')}-${get(
                this.props.export,
                'selectedMarket.label'
              )}-${get(
                this.props.export,
                'selectedEntity.label'
              )}-synthese-table-dn`}
              targetElement="#synthese-table-dn-tp"
              jpg
              pdf
              pdfOrientation="portrait"
              path="/distribution/synthesis/xls?"
              options={optionsExportXls}
              newApi
            />
          }
        />
        <Wrapper>
          <TableContainer>
            <Table columns={columns} data={data} sorted={sorted} />
          </TableContainer>
          {get(selectedIndicator, 'value') !== 'websites' &&
            this.renderSideInfo()}
        </Wrapper>
      </Container>
    );
  }
}
